import { put, call, select, cancelled, all } from 'redux-saga/effects';

import { navigate } from '@/domain/router/redux/reduxActions';
import { storage } from '@/lib/storage';
import ROUTES_PATH from '@/router/routesPathConfig';
import { generatePath } from 'react-router-dom';
import { clearOrganization } from '@/domain/organization';
import { allPayModuleCompanyPermissionsSelector, backSearchUrlSelector, persistedBackUrlSelector } from '@/domain/env';
import { backUrlSelector } from '@/domain/router';
import Api, { doLogout } from '@/domain/api';
import { setUser } from '@/lib/errorLogger';
import * as action from '@/domain/env/envActions';
import * as selector from '@/domain/env/envSelector';
import { documentSearchAction, isStoreOnAnotherCompany } from '@/domain/documents';
import { appsAdapter, featuresAdapter, payModuleCompanyPermissionsAdapter } from '@/domain/env/adapters';

import { yieldSuccessNotification, yieldErrorNotification, yieldValidationNotification } from '@/lib/toasts';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import { STUB_TOKEN } from '@/domain/constants';
import { nullifyTokenRefreshTime } from '@/initializers/awsAmplify/helpers';
import * as Companies from '@/domain/companies/sagas';
import { envApi } from '@/domain/env/api';
import { showApiErrorToast } from '@/services/api';

export { action, selector };

export function* ensureSignGetUserProfile() {
  try {
    // getting apps here and setting here is enough
    const data = yield call(envApi.getUserProfile);
    const profileData = data.AP.profile;
    setUser(profileData.userGUID, profileData.userID);

    // set externalUserId which is equal to sendbird userId which is equal to user's email
    window.OneSignal = window.OneSignal || [];
    const { OneSignal } = window;
    if (OneSignal) {
      OneSignal.push(() => {
        OneSignal.setExternalUserId(profileData.userID);
      });
    }

    const { displayName, chatToken, userID, isDokkaSupport } = profileData;

    storage().chatUser().set(displayName);
    storage()
      .chatToken()
      .set(chatToken || '');
    storage().userId().set(userID);
    storage().userGUID().set(profileData.userGUID);

    storage().isDokkaSupport().set(isDokkaSupport);

    // execute this after profile is retrieved, when router has been rendered or
    // specific saga has worked out
    // this is initialized on app start from LS for SSO
    const backUrl = yield select(backSearchUrlSelector);
    const routingBackUrl = yield select(backUrlSelector);
    const persistedBackUrl = yield select(persistedBackUrlSelector);
    const defaultGhostURL = generatePath(ROUTES_PATH.ORGANIZATIONS.absolute);

    const redirectUrl = persistedBackUrl || backUrl || routingBackUrl;

    if (routingBackUrl === '/' && isDokkaSupport) {
      yield put(navigate.replace(defaultGhostURL, {}));
    } else if (redirectUrl) {
      yield put(navigate.replace(redirectUrl, {}));
      storage().backUrl().set('');
    }
    yield put({
      type: action.signInAction.success,
      // to be removed
      payload: { dokkaToken: storage().token().get() || STUB_TOKEN, ...profileData, apps: appsAdapter(data) },
    });
  } catch (err) {
    showApiErrorToast(err);
  }
}

// eslint-disable-next-line require-yield
export function* storeDokkaToken(dokkaToken) {
  storage().token().set(dokkaToken);
}

export function* cognitoAuthHandler() {
  // yield call(storeDokkaToken, STUB_TOKEN);
  yield call(ensureSignGetUserProfile);
}

export function* ensureSignInWithUserProfile(args) {
  yield put({ type: action.signInAction.request });
  const locale = yield select(selector.localeSelector);

  try {
    const { data: signInData } = yield call(Api.signIn, { ...args, data: args.data.set('language', locale) });

    yield call(storeDokkaToken, signInData.dokkaToken);
    yield call(ensureSignGetUserProfile);
  } catch (err) {
    // @to-do check this
    // as we pass signInAction to function, it will triger signInAction.failure with proxied error
    // while we dont know what type of error it is and signInAction.failure assums exact
    // type of error and will retrieve err.response.data

    // @to-do check for not axios error type instead check by response field
    if (typeof err.response === 'undefined') {
      err.response = { data: 'Internal error. Try reloading the page' };
    }
    yield doLogout(action.signInAction, err);
  }
}

export function* cleanStorageDataOnSignout() {
  window.OneSignal = window.OneSignal || [];
  const { OneSignal } = window;

  storage().token().remove();
  storage().chatToken().remove();
  storage().chatUser().remove();
  storage().userId().remove();
  storage().isDokkaSupport().remove();

  // this removes last token refresh time
  nullifyTokenRefreshTime();

  OneSignal.push(() => {
    OneSignal.removeExternalUserId();
  });
  // this might be required for SSO user also
  // however initial Auth0 implementation didnt invoke this

  yield put({
    type: clearOrganization.type,
  });
}

export function* ensureSignOut(data = {}) {
  const { payload } = data;

  if (!(payload && payload.reason === process.env.REACT_APP_ACCESS_DENIED_CODE)) {
    storage()
      .backUrl()
      .set(window.location.pathname + window.location.search);
  }

  yield call(cleanStorageDataOnSignout);

  yield put({
    type: clearOrganization.type,
  });
  // this action must be the last call in this func as it is listened to
  // in watchSignOut() saga and cancels this task once caught, so every side-effect
  // like LS modifications/other actions must be invoked before

  yield put({
    type: action.signOutAction.success,
    ...(payload && { payload }), // this is set when we are logged out by 403 interceptor
  });
}

export function* ensureUpdateWorkSpace({ payload }) {
  try {
    const workSpaceStr = yield storage().workSpaceType().get();
    const workSpace = workSpaceStr ? JSON.parse(workSpaceStr) : {};
    workSpace[payload.companyId] = payload.type;
    yield storage().workSpaceType().set(JSON.stringify(workSpace));
    if (payload.type === 'tile') {
      yield put({
        type: documentSearchAction.type,
        payload: {},
      });
    }
  } catch (err) {
    console.log('error when save workspace type in localstore');
  }
}

export function* ensureUpdateUserFeatures({ values, resolve, reject }) {
  try {
    const { data } = yield call(Api.updateUserFeatures, { data: values });
    yield put({
      type: action.updateUserFeaturesAction.success,
      payload: featuresAdapter(data),
    });

    yieldSuccessNotification(
      <FormattedMessage id="toast.preferences.updated" defaultMessage="Notification settings are updated" />,
    );

    if (typeof resolve === 'function') {
      resolve(data);
    }
  } catch (err) {
    yield doLogout(action.updateUserFeaturesAction, err);
    yieldValidationNotification(err);
    if (typeof reject === 'function') {
      reject(err);
    }
  } finally {
    if (yield cancelled()) {
      yield put({
        type: action.updateUserFeaturesAction.failure,
        err: 'cancelled',
      });
    }
  }
}

export function* requestAccessToModule({ payload: { payload: ticketPayload, resolve, reject } }) {
  try {
    yield call(Api.createSupportTicket, { data: ticketPayload });
    yieldSuccessNotification(
      <FormattedMessage
        id="app.selector.ticketCreated"
        defaultMessage="We have received your inquiry and will respond shortly"
      />,
    );
    resolve();
  } catch (err) {
    yield doLogout(action.requestAccessToModuleAction, err);
    yieldErrorNotification(
      <FormattedMessage
        id="app.selector.inquiryError"
        defaultMessage="We encountered temporary error sending your inquiry"
      />,
    );
    reject();
  }
}

export function* ensureGetPayModuleCompanyPermissions(companyId) {
  try {
    const isAnotherCompany = yield select(isStoreOnAnotherCompany(companyId));
    if (isAnotherCompany) {
      yield all([call(Companies.ensureGetCompanies), call(Companies.ensureCompanySignIn, companyId)]);
    }

    const payModuleCompanyPermissions = yield select(allPayModuleCompanyPermissionsSelector);

    if (!payModuleCompanyPermissions.size || isAnotherCompany) {
      yield put({
        type: action.getPayModuleCompanyPermissionsAction.request,
      });
      const { data } = yield call(Api.getMyPermissionsForPayModule);

      yield put({
        type: action.getPayModuleCompanyPermissionsAction.success,
        payload: payModuleCompanyPermissionsAdapter(data),
      });
    }
  } catch (err) {
    yield doLogout(action.getPayModuleCompanyPermissionsAction, err);
  }
}
